import { useEffect, useSyncExternalStore } from 'react'
import { ErroRequisicao } from '../api/cliente'
import { IconeX } from './icones'

/**
 * O aviso de ação recusada que a tela não mostrou (ADR 0074).
 *
 * Muitas ações terminavam caladas quando o servidor recusava: excluir campo em uso, desconectar
 * número, mover etapa pelo alto da conversa, salvar a ficha, tirar apoio. O clique parecia não
 * ter pegado. Em vez de pôr um aviso em cada tela, toda mutação que falha passa por aqui
 * (`MutationCache`, em `main.tsx`) e a mensagem do servidor aparece no alto, uma vez.
 *
 * Sem repetir o que a tela já diz: depois de a tela ter tido tempo de desenhar o erro, se o texto
 * da recusa já está à vista (no `Aviso` da janela, embaixo do campo), este aviso não aparece. Fica
 * de fora também a sessão expirada (401), que leva à tela de entrada, e a mutação marcada com
 * `meta: { silenciosa: true }`.
 */

interface Recusa {
  id: number
  texto: string
}

let recusas: Recusa[] = []
let proximoId = 1
/** Fora do sistema (tela de entrada, login) não há onde mostrar, e a recusa não fica guardada. */
let montados = 0
const ouvintes = new Set<() => void>()

function avisar() {
  for (const ouvinte of ouvintes) ouvinte()
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

function dispensar(id: number) {
  recusas = recusas.filter((r) => r.id !== id)
  avisar()
}

/** O texto que a pessoa lê para este erro. */
function textoDoErro(erro: unknown): string | null {
  if (erro instanceof ErroRequisicao) {
    if (erro.status === 401) return null
    return erro.corpo.mensagem || 'O servidor recusou a ação.'
  }
  return 'Não consegui falar com o servidor. Confira a conexão e tente de novo.'
}

/**
 * Chamado pelo `MutationCache` em toda mutação que falha. Espera a tela reagir ao erro e só então
 * decide se a recusa precisa de aviso próprio.
 */
export function avisarRecusa(erro: unknown, silenciosa = false): void {
  if (silenciosa) return
  const texto = textoDoErro(erro)
  if (!texto) return

  window.setTimeout(() => {
    if (montados === 0) return
    // A tela já mostra esta recusa: o aviso seria a mesma frase duas vezes.
    if (document.body.innerText.includes(texto)) return
    // A mesma recusa duas vezes seguidas (duplo clique) vira um aviso só.
    if (recusas.some((r) => r.texto === texto)) return
    recusas = [...recusas.slice(-2), { id: proximoId++, texto }]
    avisar()
  }, 400)
}

export function AvisosDeRecusa() {
  const lista = useSyncExternalStore(assinar, () => recusas)
  useEffect(() => {
    montados++
    return () => {
      montados--
    }
  }, [])
  if (lista.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-16 z-[80] flex flex-col items-center gap-2 px-4"
      role="alert"
    >
      {lista.map((recusa) => (
        <CartaoDaRecusa key={recusa.id} recusa={recusa} />
      ))}
    </div>
  )
}

function CartaoDaRecusa({ recusa }: { recusa: Recusa }) {
  // Some sozinho: é um recado, não uma pendência. O X fecha antes.
  useEffect(() => {
    const relogio = window.setTimeout(() => dispensar(recusa.id), 8000)
    return () => window.clearTimeout(relogio)
  }, [recusa.id])

  return (
    <div className="aviso-entrando pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl bg-red-50 px-4 py-3 shadow-media ring-1 ring-red-200">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-red-800">Não foi feito</p>
        <p className="text-xs text-red-700">{recusa.texto}</p>
      </div>
      <button
        type="button"
        onClick={() => dispensar(recusa.id)}
        aria-label="Fechar o aviso"
        title="Fechar"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-red-700 transition hover:bg-red-100"
      >
        <IconeX className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
